/**
 * Steam-facts slice — the catalog, the live-search results, the art asset cache
 * and the stale-while-revalidate machinery of docs/ARCHITECTURE.md §5.3–§5.4.
 *
 * Revalidation triggers, matching the prototype's call sites:
 *   boot / focus / visibilitychange → `revalidateSurface(visible appids)`
 *   a screenful of tiles renders    → `queueAssets(appids)` — the same entry
 *   detail open                     → `enrichGame(appid)`          (`enrich`)
 *   search input                    → `searchLive(q)`              (`fetchLive`)
 *
 * When the Worker is down the repository answers from `data/fixtures`, the UI
 * keeps its data, and the freshness chip flips to RETRYING — the chip is the one
 * place that tells the truth about round-trips, which is the whole point of
 * §5.4 and what retires INVENTORY §12.3.
 */

import type { StateCreator } from 'zustand';

import type { CardValue } from '../../../../shared/cardFields.ts';
import { PATCH_KEYS, parseItemRecord } from '../../../../shared/cardFields.ts';
import type { CcuSample, PriceSample } from '../../lib/logic.ts';
import type { DetailFacts } from '../../repo/Repository.ts';
import type { CrewOwnershipRow, PlayerOverride } from '../../repo/Repository.ts';
import type { AssetMap, Game, GameId, GameMedia, SteamFactPatch } from '../../lib/types.ts';
import type { EnrichResult } from '../../repo/Repository.ts';
import type { CcuMap } from '../../repo/steamApi.ts';
import { CCU_MAX_BATCH, fetchLiveCcu, LIVE_ROW_DESC, sameOriginCcu } from '../../repo/steamApi.ts';
import { backoffMs, OFFLINE_AFTER } from './freshness.ts';
import type { QdStore } from '../types.ts';

/** §5.3: the prototype's 500 ms coalesce window and 50-appid cap both stay. */
export const GI_COALESCE_MS = 500;
export const GI_MAX_BATCH = 50;
/** §5.3: "≤1 call per 3 s". */
export const SAVE_FIX_DEBOUNCE_MS = 3000;
/**
 * How long a detail-open enrich counts as "already done" for this appid. The
 * volatile set (price, discount, release, demo) does not move on a minute
 * scale; re-asking on every re-open of the same tile was pure traffic.
 *
 * It gates `enrichGame` and NOTHING ELSE. `ensureMedia` fetches on its own
 * condition — "`media[appid]` is absent" — precisely so that an appid this TTL
 * considers fresh can never leave the carousel permanently empty.
 */
export const ENRICH_TTL_MS = 10 * 60_000;
/**
 * How long one appid counts as revalidated, whoever asked — the dedupe that
 * replaces the `s.assets` check `queueAssets` used to make (see
 * `revalidateSurface` for why that check disabled the whole ladder).
 *
 * A TIME STAMP IS THE ONLY HONEST KEY HERE. What a revalidation refreshes is
 * price, discount, ribbons and review percent, and none of those can be inferred
 * from anything already in the store: the question "is what I hold stale?" has
 * no answer but "how long ago did I ask?".
 *
 * The number mirrors `boot.ts#REVALIDATE_MIN_MS` on purpose — that floor already
 * exists to stop repeated alt-tabbing from hammering the endpoint, and a second,
 * different floor one layer down would make the effective rate a puzzle. This
 * one is per APPID rather than per pass, so it also covers the callers boot's
 * floor never saw: a LOAD MORE that re-reveals the rows above it, and a filter
 * toggled off and straight back on.
 */
export const SURFACE_MIN_MS = 15_000;
/**
 * How many appids of one surface get a live count.
 *
 * `CCU_MAX_BATCH` exactly, so a pass is ONE Worker invocation: that route fans
 * out one upstream call per appid (GetNumberOfCurrentPlayers takes no batch), so
 * 12 appids is 12 reads + 12 fetches + 12 writes against the free plan's
 * 50-subrequest budget, and a second chunk would double it for an overlay that
 * is cosmetic. `revalidateSurface` passes the HEAD of the surface, which is the
 * part of a grid a user is actually looking at when the pass fires.
 */
export const CCU_SURFACE_MAX = CCU_MAX_BATCH;

export interface SteamFactsSlice {
  catalog: Game[];
  live: Game[];
  liveState: '' | 'busy' | 'ok' | 'err';
  liveSrc: 'steam' | 'cs';
  assets: AssetMap;
  facts: Record<string, SteamFactPatch>;
  /**
   * appid → trailers + screenshots, for the media carousel.
   *
   * SESSION-ONLY, and deliberately so. It is Steam-derived, so writing it back
   * would put upstream data in a curated table (decision D2) — `steam_facts` is
   * the only Steam write path there is and it has no media columns. Losing it on
   * reload costs one appdetails request, which is the request a Detail open
   * makes anyway.
   */
  media: Record<number, GameMedia>;
  /**
   * appid → the 90-day CCU series behind the Detail chart.
   *
   * Keyed by **appid**, like `media`, so a live-search row shares the cache
   * entry with the catalog row it becomes once someone adds it.
   *
   * `sample` is the honesty flag. `SupabaseRepository` always answers `false`
   * (it returns measurements or nothing); `LocalRepository` always answers
   * `true`. It is the only thing that puts the `SAMPLE` chip on the chart, so a
   * production series can never render as anything but what it is.
   *
   * Absent means "not asked yet" and drives the loading state; a present entry
   * with `points: []` means asked and there is nothing to draw, which is a
   * different screen.
   */
  ccuSeries: Record<number, { points: CcuSample[]; sample: boolean }>;
  /**
   * Per-appid 12-month price series (migration 3045). Separate map from
   * `ccuSeries` rather than a widened one: the two are fetched independently,
   * fail independently, and a game can legitimately have one and not the other
   * — a free game has no price history and a brand-new one has no players yet.
   */
  priceSeries: Record<number, { points: PriceSample[]; sample: boolean }>;
  /**
   * Per-appid Detail-only `steam_facts` columns (3048). `null` is a CACHED
   * answer meaning "asked, got nothing" — presence in the map, not truthiness,
   * is what stops a re-ask, the same rule `ovr` follows.
   */
  detailFacts: Record<number, DetailFacts | null>;
  /**
   * appid → who in the crew owns it (migration 3007).
   *
   * Absent means not asked; a present empty array means asked and the RPC had
   * nothing to say. The roster renders a pending column for the first and
   * nothing for the second, so the two must not be conflated.
   */
  crewOwns: Record<number, CrewOwnershipRow[]>;
  /**
   * appid → who in the crew owns it, by `User.k` — the OWNED BY filter's data
   * (migration 3046). The bulk sibling of `crewOwns`, kept SEPARATE from it on
   * purpose: that one is a per-appid roster read that has to distinguish
   * `linked` from `owned` for the Detail column, this one is owners and nothing
   * else, and merging them would let a cheap bulk answer silently satisfy a
   * guard that wants the roster.
   *
   * ABSENT AND `[]` ARE DIFFERENT AND `passFlt` DEPENDS ON IT. Absent means
   * nobody has asked about that appid, and the filter must not hide a row on
   * data it does not have (decision 5). `[]` means we asked and nobody owns it,
   * which DOES hide it. `ensureOwnedBy` therefore seeds every appid it requests
   * before merging the answer, so a fetch that comes back with three owners
   * across a hundred games leaves ninety-seven honest `[]`s rather than
   * ninety-seven silences.
   *
   * `linked` is NOT derivable from this and must never be inferred from it — an
   * unlinked member owns nothing here, and reading their absence as `does not
   * own` is deviation P2-08. `crewLinks` is the link fact.
   */
  ownBy: Record<number, string[]>;
  /**
   * appid → MY lifetime playtime in minutes, or `null` for "asked, and I do not
   * own it" — the BACKLOG's PLAYED / OWNED / UNOWNED tabs.
   *
   * THREE STATES AGAIN, and the tabs depend on all three the way `passFlt`
   * depends on `ownBy`'s two. Absent is NOT ASKED and puts a game in no tab but
   * ALL; `null` is asked-and-unowned and is the only thing UNOWNED may show;
   * a number is owned, and `> 0` is the whole of PLAYED. Collapsing absent into
   * `null` would file an entire shelf under UNOWNED for the seconds before the
   * read lands, and — on a failed read — for the rest of the session.
   *
   * `0` IS A REAL ANSWER, not an absence: owned and never launched. It is why
   * this is `number | null` rather than a truthy count, and why PLAYED tests
   * `> 0` rather than testing the entry.
   *
   * SELF-ONLY BY CONSTRUCTION. `fetchMyLibrary` reads `steam_owned_apps`, whose
   * RLS policy admits only the caller's rows, so nothing crew-wide can arrive
   * here — the neighbouring `ownBy` is the crew question and keeps its own RPC.
   */
  myLib: Record<number, number | null>;
  /**
   * user key → has that member linked Steam (migration 3010).
   *
   * `null` — not the empty object — is "not asked yet", and the Friends roster
   * renders that as a blank chip rather than `STEAM NOT LINKED`. The distinction
   * is the entire point of the field: reporting NOT LINKED about someone we have
   * not asked about is precisely the bug this replaced (deviation P2-08), so the
   * pending state must stay reachable and must stay distinguishable from a real
   * `false`. A member missing from a non-null map is pending for the same reason.
   *
   * It stays `null` on failure too, so a broken read retries on the next visit
   * instead of freezing a fabrication into the session.
   *
   * YOUR OWN CARD DOES NOT READ THIS — see `crewVM`. `steamLink` is the live
   * self answer and it is the one entry that can change mid-session.
   */
  crewLinks: Record<string, boolean> | null;
  /**
   * appid → who set its player-count override, or `null` for "asked, there
   * isn't one" (migration 3008).
   *
   * **Absence and `null` are different and the guard must test PRESENCE.**
   * `crewOwns` above gets away with a truthy check because its empty answer is
   * `[]`; here the empty answer is `null`, so `if (get().ovr[appid])` never
   * short-circuits and re-issues the RPC on every render of the rail, forever.
   */
  ovr: Record<number, PlayerOverride | null>;
  /**
   * appid → the live player count this session last asked Steam for.
   *
   * SESSION-ONLY AND DISPLAY-ONLY, and kept in its own map rather than on the
   * row for both halves of that. It never reaches `saveFix`: `SteamFactPatch`
   * has no `ccu` field, so nothing that could carry it to `upsert_steam_facts`
   * even exists — the write path could not persist a live count if a future
   * caller tried. And it never overwrites `Game.ccu`, which is the NIGHTLY
   * PERSISTED SAMPLE that `popOf()` ranks on and `liveStatsOf` ages: one instant
   * off GetNumberOfCurrentPlayers and a swept sample are different measurements
   * (lib/types.ts spells out why), and merging them would change local-mode
   * ordering under the member every time a tile refreshed.
   *
   * ABSENT ≠ `{ n: null }`, exactly as in `CcuMap`: absent is "never answered",
   * `n: null` is "asked, and Steam had no count", `n: 0` is a real measurement.
   *
   * NOTHING RENDERS IT YET, deliberately — Phase 2 deferred the tile decision to
   * a design pass, and this slice wires the data so that pass stays a rendering
   * question.
   */
  liveCcu: Record<string, LiveCount>;
  /**
   * The live-count transport. A store field so a test can drive it, and so the
   * overlay stays OFF the `Repository` interface: every method there is a data
   * path that can be written back, and keeping this one out is what makes "a
   * live count is never persisted" structural rather than a discipline.
   */
  ccuFetch: (appids: number[]) => Promise<CcuMap>;

  gById: (id: GameId) => Game | null;
  gByAppid: (appid: number) => Game | null;

  searchLive: (q: string) => Promise<void>;
  enrichGame: (appid: number) => Promise<void>;
  /** Fetch media for `appid` unless we already have it. Fire-and-forget. */
  ensureMedia: (appid: number) => void;
  /**
   * Fetch the 90-day CCU series for `appid` unless we already have it.
   * Fire-and-forget, same shape as `ensureMedia`.
   *
   * Not TTL-gated the way `enrichGame` is: the samples move twice a day, so
   * one fetch per appid per session is already far more conservative than the
   * data, and re-opening the same game is the common case this saves.
   */
  ensureCcuSeries: (appid: number) => void;
  ensurePriceSeries: (appid: number) => void;
  ensureDetailFacts: (appid: number) => void;
  /** Fetch crew ownership for `appid` unless we already have it. */
  ensureCrewOwnership: (appid: number) => void;
  /**
   * Fill `ownBy` for every appid in `appids` this session has not asked about.
   * Fire-and-forget; the OWNED BY filter widens until it answers.
   *
   * Called from the filter panel's open and from every filter mutator that
   * touches OWNED BY, which is enough: the panel is the only place the filter
   * can be set, and it states the match count that has to be right.
   */
  ensureOwnedBy: (appids: readonly number[]) => void;
  /**
   * Fill `myLib` for every appid in `appids` this session has not asked about.
   * Fire-and-forget; the BACKLOG's ownership tabs widen until it answers.
   *
   * Called from both BACKLOG screens on render with the shelf's appids — a
   * render path, unlike `ensureOwnedBy`'s panel-open, because the tabs are on
   * the page itself and the counts beside them have to be right on arrival
   * rather than after a click. The in-flight set is what keeps that from
   * becoming a request per render.
   */
  ensureMyLibrary: (appids: readonly number[]) => void;
  /**
   * Fetch the crew's Steam-link roster unless this session already has it.
   * Called from `nav('crew')` — a visit, not a render, which is what lets a
   * failure stay uncached and simply be re-asked next time.
   */
  ensureCrewLinks: () => void;
  /** Fetch the override's attribution once per appid per session. */
  ensurePlayerOverride: (appid: number) => void;
  /** Replace one appid's cached attribution after a write. */
  setPlayerOverrideCache: (appid: number, o: PlayerOverride | null) => void;
  /**
   * Refresh everything volatile about the appids ON SCREEN — art, ribbons,
   * price, review percent and the live-count overlay. THE §5.3/§5.4 entry point.
   */
  revalidateSurface: (appids: number[]) => Promise<void>;
  /** The tile-viewport name for `revalidateSurface`. Fire-and-forget. */
  queueAssets: (appids: number[]) => void;
  revalidate: (appids: number[]) => Promise<void>;
  cancelRevalidate: () => void;
}

/** One appid's live player count, and when we asked for it. */
export interface LiveCount {
  /** Players right now. `null` = we asked and Steam had no count for it. */
  n: number | null;
  /** Epoch ms of the answer — the overlay's own `ccuAt`, never the row's. */
  at: number;
}

/**
 * `coalesce(fact, row)` — defined fields win, undefined ones leave the row alone.
 *
 * TWO FIELDS BREAK THAT RULE AND ONLY FILL A HOLE: `dev` and `ds`. Both have
 * curated twins (decision D2 — `catalog_games.dev` and `.description` are the
 * crew's, and `discover_page` already prefers `game_features.developer` where
 * one exists), so a Steam string may fill a blank but may never overwrite an
 * editorial one. `hasText` is what draws that line, and it treats the two
 * "nobody told us" sentinels a thin row carries — `''` and the `'—'` that
 * `toBrowseGame` / `toLiveGame` write — as the holes they are.
 *
 * Everything else assigns straight through: `pcats` has no curated twin, and
 * the ribbon flags, price and rating are Steam's to state by definition.
 */
const hasText = (v: string | undefined): boolean => v !== undefined && v !== '' && v !== '—';

export const applyFact = (g: Game, f: SteamFactPatch): Game => {
  const out = { ...g };
  if (f.p !== undefined) out.p = f.p;
  if (f.o !== undefined) out.o = f.o;
  if (f.up !== undefined) out.up = f.up;
  if (f.ea !== undefined) out.ea = f.ea;
  if (f.demo !== undefined) out.demo = f.demo;
  if (f.dAid !== undefined) out.dAid = f.dAid;
  if (f.rd !== undefined) out.rd = f.rd;
  // Straight assignment, NOT the hole-filling treatment `writebackGaps` gives
  // `rd`: this only ever arrives from an appdetails read, which is the most
  // authoritative source there is for it, so a later answer supersedes an
  // earlier one rather than deferring to it.
  if (f.rdFull !== undefined) out.rdFull = f.rdFull;
  if (f.rp !== undefined) out.rp = f.rp;
  // Steam's rung, beside its percentage and from the same items read. Both are
  // display-and-filter only — migration 2909 closed the write-back path.
  if (f.rs !== undefined) out.rs = f.rs;
  if (f.pcats !== undefined) out.pcats = f.pcats;
  if (f.dev !== undefined && !hasText(g.dev)) out.dev = f.dev;
  // The live-search placeholder is a hole too: it exists precisely to say "open
  // this and the real text loads", so a real short description supersedes it.
  if (f.ds !== undefined && (!hasText(g.ds) || g.ds === LIVE_ROW_DESC)) out.ds = f.ds;
  return out;
};

/**
 * Everything this store already knows about `g.appid`, applied to a freshly
 * fetched live-search row.
 *
 * Live rows come off `storesearch` / CheapShark, neither of which reports
 * release state, so `repo/steamApi.ts#toLiveGame` hard-codes
 * `up/ea/demo = false`. That is why a search tile used to render with no
 * ribbon even for a game whose flags this session had already learned — and
 * why every new search wiped the ones a detail-open had filled in
 * (`searchLive` replaces `live` wholesale).
 *
 * Two overlays, in precedence order:
 *   1. a catalog twin, if there is one — `v_games` joined `steam_facts`
 *      server-side, so its flags are the crew-shared truth. ONLY the four
 *      ribbon fields are copied: price, name and editorial text on a live row
 *      are the live source's business, not the catalog's.
 *   2. `facts[appid]` — this session's appdetails / GetItems answers, which are
 *      newer than the catalog fetch by construction.
 */
export function withKnownFacts(
  g: Game,
  catalog: readonly Game[],
  facts: Readonly<Record<string, SteamFactPatch>>,
): Game {
  if (!(g.appid > 0)) return g;
  let out = g;
  const twin = catalog.find((c) => c.appid === g.appid);
  if (twin) out = { ...out, up: twin.up, ea: twin.ea, demo: twin.demo, dAid: twin.dAid };
  const f = facts[String(g.appid)];
  return f ? applyFact(out, f) : out;
}

/**
 * What an items response actually stated, as one patch per appid.
 *
 * THE VALIDATION IS THE REGISTRY'S, not this file's. `parseItemRecord` applies
 * each enabled field's own validator from `shared/cardFields.ts` — the same
 * table, and the same functions, the Worker uses to rebuild a record out of the
 * shard cache. That is the point of sharing it: a value the Worker considered
 * well-formed enough to cache and a value the client considers well-formed
 * enough to render can no longer be two different judgements. The three fields
 * this used to type `unknown` and re-check by hand are still re-checked; the
 * check just is not written twice any more.
 *
 * ONLY DEFINED FIELDS ARE PICKED UP, and that rule is load-bearing rather than
 * tidy. Every optional field on the record distinguishes "the response said
 * nothing" from "the response said false/none": an appid whose record is pure
 * art produces no patch at all, a response that omits `demo` can never clear a
 * `demo: true` an appdetails enrich established (GetItems has no way to report
 * the negative), and a response that omits `p` can never turn a healed price
 * back into TBA.
 *
 * That last one is why an explicit `null` price is dropped too, unlike a stated
 * `false` flag: the Worker omits price for an unlisted app rather than nulling
 * it, so a null arriving here is a shape we did not agree to, and the safe
 * reading of an unexpected shape is silence. `parseDollars` rejects it by type.
 */
export function itemPatches(assets: AssetMap): Record<string, SteamFactPatch> {
  const out: Record<string, SteamFactPatch> = {};
  for (const [key, raw] of Object.entries(assets)) {
    const appid = Number(key);
    if (!Number.isSafeInteger(appid) || appid <= 0) continue;
    const fields = parseItemRecord(raw);
    if (fields === null) continue;
    const p: SteamFactPatch = { appid };
    let stated = 0;
    for (const k of PATCH_KEYS) {
      const v = fields[k];
      if (v === undefined) continue;
      // The cast is narrow and the registry is what makes it sound: every key
      // in `PATCH_KEYS` is a key of `SteamFactPatch` (asserted in `lib/types.ts`
      // against `AssetRec`, which declares the same set), and the value came
      // out of that key's own validator, so its runtime type is the declared
      // one. `steamFacts.test.ts` pins both halves of that claim.
      (p as unknown as Record<string, CardValue>)[k] = v;
      stated += 1;
    }
    // `appid` alone is not a patch — that record was pure art.
    if (stated > 0) out[key] = p;
  }
  return out;
}

/**
 * The subset of `patch` worth sending to `upsert_steam_facts` — the fields this
 * row does not already have.
 *
 * WHY A GAP FILTER AND NOT THE WHOLE PATCH. Routing the surface refresh into
 * the self-heal write path is what makes one member's scroll fill in the
 * crew-shared row, which is the point. Sending the FULL patch would also work
 * and would be wrong, for a reason that only shows up two layers away:
 * `upsert_steam_facts` NULLs `volatile_at` on any write carrying one of 2903's
 * volatile six, and `pool_facts_targets` sorts `nulls first`. A member paging
 * through 300 rows would therefore move all 300 to the head of the nightly
 * sweep's queue — rows the sweep has already priced correctly, re-asserted from
 * the same upstream API seconds earlier — and the budget-bounded walk would
 * spend the night re-verifying them instead of reaching the rows it never got
 * to. The write would be harmless and the queue starvation would not be.
 *
 * So only a genuine gap is written, which is also the only case that changes
 * anything: a `null` price or an empty release month. `o` rides along with `p`
 * because "priced at X, not discounted" is one fact in two columns and writing
 * half of it would leave a stale discount beside a fresh price.
 *
 * The ribbon flags are deliberately absent. They are never *missing* on a row —
 * `up`/`ea`/`demo` are booleans that always have a value — so there is no gap
 * to detect, and re-asserting them on every pass is the exact traffic this
 * filter exists to prevent. The pool sweep owns keeping them current.
 */
export function writebackGaps(row: Game | undefined, patch: SteamFactPatch): SteamFactPatch | null {
  if (!row) return null;
  const out: SteamFactPatch = { appid: patch.appid };
  let stated = 0;
  if (patch.p !== undefined && patch.p !== null && row.p === null) {
    out.p = patch.p;
    if (patch.o !== undefined) out.o = patch.o;
    stated += 1;
  }
  if (patch.rd !== undefined && row.rd === '') {
    out.rd = patch.rd;
    stated += 1;
  }
  return stated > 0 ? out : null;
}

export const createSteamFactsSlice: StateCreator<QdStore, [], [], SteamFactsSlice> = (set, get) => {
  // Per-store, not per-module: two stores (two "tabs" in a test) must not share
  // a coalesce queue or a retry timer.
  let giQ: number[] = [];
  let giT: ReturnType<typeof setTimeout> | null = null;
  /**
   * batch key → that batch's pending retry. **PER BATCH, not one handle.**
   *
   * A single module-level timer was correct only while one batch could be in
   * flight at a time, and `revalidateSurface` ended that: it queues the tail
   * through `giQ`/`giT` and awaits the head in the same call. `revalidate`'s
   * first act is to cancel the pending retry, so with one shared handle the head
   * armed its retry and the tail's drain 500 ms later DESTROYED it — leaving the
   * off-screen rows with a working `1500·2ⁿ` ladder and the visible ones with
   * none. Both sets are already stamped in `revalAt`, so nothing re-asks them:
   * a Worker outage froze exactly the tiles a member was looking at, and froze
   * them silently, since a stale price renders like a fresh one.
   *
   * Keyed on the appid list because that is what a retry re-asks — the retry
   * belongs to the request, so cancelling it is something only the same request
   * may do. The map is bounded by the same thing that bounds the queue: an entry
   * is deleted when its timer fires, when its batch is re-issued, and on
   * teardown, and no entry is ever added past `OFFLINE_AFTER`.
   */
  const retryT = new Map<string, ReturnType<typeof setTimeout>>();
  let fixQ: SteamFactPatch[] = [];
  let fixT: ReturnType<typeof setTimeout> | null = null;
  /**
   * appid -> when `revalidateSurface` last ASKED about it. The dedupe for every
   * surface pass; see that action for why the old one was worse than useless.
   */
  const revalAt = new Map<number, number>();
  /** appid -> `at` of the last SUCCESSFUL enrich in this store. */
  const enriched = new Map<number, number>();
  /**
   * appids `ensureOwnedBy` has asked about and not yet heard back on.
   *
   * Not derivable from `ownBy`, which is exactly why it exists: an appid is
   * absent from that map both before the request goes out and while it is in
   * the air, and only this tells the two apart. Without it, the panel's open
   * and the first OWNED BY click would each request the whole catalog.
   */
  const ownByInFlight = new Set<number>();
  /**
   * The same guard for `ensureMyLibrary`, and separate from `ownByInFlight`
   * because the two reads answer different questions off different doors — a
   * shared set would let a crew-ownership request in flight suppress the
   * self-library one that the BACKLOG tabs are actually waiting on.
   */
  const myLibInFlight = new Set<number>();
  /**
   * appid -> the `appdetails` round trip currently in the air. Both entry points
   * share it, which is what a Detail open depends on: `openGame` starts an
   * enrich and the carousel mounts a tick later and calls `ensureMedia`, so
   * without this the one screen would spend two requests on the same payload.
   */
  const inFlight = new Map<number, Promise<void>>();

  /**
   * The write half of a successful `appdetails` round trip, shared by BOTH
   * fetch paths (`enrichGame` and `ensureMedia`) so that whichever one actually
   * spends the request populates `facts` AND `media` from it.
   */
  const applyEnrich = (appid: number, res: EnrichResult): void => {
    enriched.set(appid, Date.now());
    const patch = res.patch;
    set((st) => ({
      facts: { ...st.facts, [String(appid)]: { ...st.facts[String(appid)], ...patch, appid } },
      catalog: st.catalog.map((g) => (g.appid === appid ? applyFact(g, patch) : g)),
      live: st.live.map((g) => (g.appid === appid ? applyFact(g, patch) : g)),
      // `feedRows` too, since `gById` answers Detail opens from it: the healed
      // price/discount/ribbons must be on the tile the user goes BACK to, not
      // only on the Detail screen that triggered the heal.
      feedRows: st.feedRows.map((g) => (g.appid === appid ? applyFact(g, patch) : g)),
      // The carousel's Steam half rides along with the fact patch, so opening a
      // Detail screen needs exactly one request, not two.
      media: { ...st.media, [appid]: res.media },
    }));
    // One debounced write-back warms the crew-shared cache (§5.3). Note what is
    // NOT in the batch: `res.media` never reaches `upsert_steam_facts` (D2).
    fixQ.push(patch);
    if (fixT) clearTimeout(fixT);
    fixT = setTimeout(() => {
      const batch = fixQ;
      fixQ = [];
      fixT = null;
      void get().repo.saveFix(batch);
    }, SAVE_FIX_DEBOUNCE_MS);
  };

  /**
   * The fetch half. Every `appdetails` request in this slice goes through here,
   * so the two callers can never race each other into two requests for one
   * payload. It never rejects: a failure is swallowed and, crucially, NOT
   * recorded in `enriched`, so the next open retries.
   *
   * THE `light` FLAG IS GONE, and with it the join rule that used to weigh two
   * requests against each other. It was dead the whole way down: `openGame`
   * derived it from `!g.live`, `enrichGame` and this function threaded it, and
   * both repositories name the parameter `_light` and fetch the identical
   * `/api/steam/app/:appid` payload either way. A light response therefore
   * carried exactly what a full one did, so "a full enrich cannot be served by a
   * light one" was suppressing nothing and licensing a SECOND round trip for a
   * payload already in hand. One request per appid per TTL, full stop.
   */
  const runEnrich = (appid: number): Promise<void> => {
    const running = inFlight.get(appid);
    if (running) return running;

    const p = (async () => {
      try {
        // The literal is the last stub of the flag. It survives only in
        // `Repository.enrich`'s signature, where `LocalRepository` and
        // `SupabaseRepository` both name it `_light` and neither reads it;
        // striking it from the interface and its three implementations is a
        // mechanical follow-up with no behaviour in it.
        const res = await get().repo.enrich(appid, false);
        applyEnrich(appid, res);
      } catch {
        // NOT recorded — a failed enrich must be retried on the next open.
      }
    })();
    inFlight.set(appid, p);
    // `p` never rejects, so `then` is the whole cleanup. The identity check
    // keeps a settled request from deleting a newer one's entry.
    void p.then(() => {
      if (inFlight.get(appid) === p) inFlight.delete(appid);
    });
    return p;
  };

  /**
   * The live-count overlay for the head of a surface. Fire-and-forget by design.
   *
   * READ-ONLY, and the comment is the contract: RANKING USES THE NIGHTLY
   * PERSISTED VALUE (`steam_facts.ccu`, swept twice daily and carried on the row
   * as `Game.ccu` / `ccuAt`), while this map is only ever a caption. It lands in
   * `liveCcu`, never on a row and never in `fixQ`, so no path exists from here to
   * `saveFix` → `upsert_steam_facts`.
   *
   * Never throws: `fetchLiveCcu` answers a dead round trip with absence, and an
   * injected transport that misbehaves is caught here, because a cosmetic
   * overlay may not take a revalidation pass down with it.
   */
  const runCcu = async (appids: number[]): Promise<void> => {
    if (!appids.length) return;
    let map: CcuMap;
    try {
      map = await get().ccuFetch(appids);
    } catch {
      return;
    }
    const keys = Object.keys(map);
    if (!keys.length) return;
    const at = Date.now();
    set((st) => {
      const liveCcu = { ...st.liveCcu };
      // `?? null` and not `|| null`: `0` is a real measurement — nobody is in
      // the game right now — and must not collapse into "Steam had no count".
      for (const k of keys) liveCcu[k] = { n: map[k] ?? null, at };
      return { liveCcu };
    });
  };

  /**
   * Flush one batch, then RE-ARM while work remains.
   *
   * The tail used to be dropped on the floor: `giQ` kept everything past
   * `GI_MAX_BATCH` with no timer pointing at it, and nothing but the *next*
   * `queueAssets` call could ever drain it. A screenful of 60 therefore fetched
   * 50 and left 10 tiles permanently blank for as long as the user stayed on the
   * screen — and each LOAD MORE made the untouched remainder larger.
   */
  const giDrain = (): void => {
    const batch = giQ.slice(0, GI_MAX_BATCH);
    giQ = giQ.slice(GI_MAX_BATCH);
    giT = null;
    void get().revalidate(batch);
    if (giQ.length) giT = setTimeout(giDrain, GI_COALESCE_MS);
  };

  return {
  catalog: [],
  live: [],
  liveState: '',
  liveSrc: 'steam',
  assets: {},
  facts: {},
  media: {},
  ccuSeries: {},
  priceSeries: {},
  detailFacts: {},
  crewOwns: {},
  ownBy: {},
  myLib: {},
  crewLinks: null,
  ovr: {},
  liveCcu: {},
  ccuFetch: (appids) => fetchLiveCcu(sameOriginCcu(), appids),

  gById: (id) => {
    const s = get();
    // `feedRows` is the third population a Detail open can come from: a feed row
    // is a full games-table row that deliberately never lands in `catalog` or
    // `live` (slices/feed.ts on why merging would break `browsable()`). Without
    // this arm most feed tiles resolved to null and `headerVM` bounced the
    // screen straight back to Discover. Catalog and live keep precedence — a
    // twin that exists in either is the same appid with at least as much data,
    // and `applyEnrich` patches all three arrays so they cannot drift apart.
    return (
      s.catalog.find((g) => g.id === id) ??
      s.live.find((g) => g.id === id) ??
      s.feedRows.find((g) => g.id === id) ??
      null
    );
  },
  // NOT extended over `feedRows`, on purpose: its callers are the playtest
  // lookups and `boards.ts`' `needsMint` set, where "no catalog/live row" is a
  // *minting* decision, not a rendering one. Answering from a transient feed
  // page there would suppress mints based on which feed page happens to be
  // loaded.
  gByAppid: (appid) => {
    const s = get();
    return s.catalog.find((g) => g.appid === appid) ?? s.live.find((g) => g.appid === appid) ?? null;
  },

  searchLive: async (q) => {
    const s = get();
    let res;
    try {
      res = await s.repo.fetchLive(q);
    } catch {
      set({ live: [], liveState: 'err' });
      return;
    }
    // Late response for a query the user has moved on from (prototype line 1635).
    if (get().q.trim() !== q) return;
    const st = get();
    const known = new Set(st.catalog.map((g) => g.id));
    // `set({ live })` is wholesale, so anything already known about these
    // appids has to be re-applied here or it is lost on every keystroke.
    const items = res.items
      .filter((g) => !known.has(g.id))
      .map((g) => withKnownFacts(g, st.catalog, st.facts));
    set({ live: items, liveState: 'ok', liveSrc: res.src });
    if (res.fromNetwork) get().freshOk();
    else get().freshFail('OFFLINE');
  },

  enrichGame: async (appid) => {
    // Already answered this session, and one answer is the whole answer — see
    // `runEnrich` on why the light/full distinction was fiction.
    const prev = enriched.get(appid);
    if (prev !== undefined && Date.now() - prev < ENRICH_TTL_MS) return;
    await runEnrich(appid);
  },

  /**
   * For any surface that shows the carousel — both Detail panels and both
   * What's New cards mount it, and only the Detail path also goes through
   * `openGame`. On that path this call lands a tick after `enrichGame` has
   * already put a request in the air, and `runEnrich` joins it rather than
   * opening a second one, so a Detail open stays at one round trip.
   *
   * It deliberately does NOT go through `enrichGame`: that would put it behind
   * ENRICH_TTL_MS, and an appid whose facts were enriched but whose `media` is
   * missing would then be stuck with an empty carousel for the rest of the TTL.
   * The condition here is the one that actually matters — "is `media[appid]`
   * missing?" — and it reaches the fetch directly.
   */
  ensureMedia: (appid) => {
    if (!Number.isSafeInteger(appid) || appid <= 0) return;
    if (get().media[appid]) return;
    void runEnrich(appid);
  },

  ensureCrewOwnership: (appid) => {
    if (!Number.isSafeInteger(appid) || appid <= 0) return;
    if (get().crewOwns[appid]) return;
    void (async () => {
      try {
        const rows = await get().repo.fetchCrewOwnership(appid);
        set((s) => ({ crewOwns: { ...s.crewOwns, [appid]: rows } }));
      } catch {
        set((s) => ({ crewOwns: { ...s.crewOwns, [appid]: [] } }));
      }
    })();
  },

  ensureOwnedBy: (appids) => {
    const have = get().ownBy;
    const want = [
      ...new Set(
        appids.filter(
          (a) => Number.isSafeInteger(a) && a > 0 && !(a in have) && !ownByInFlight.has(a),
        ),
      ),
    ];
    if (want.length === 0) return;
    // Marked in flight BEFORE the await, not after. Both filter panels call
    // this on open and every OWNED BY toggle calls it again; without this the
    // second call would re-request everything the first has not answered yet,
    // and a member clicking four names would send four overlapping reads of the
    // same catalog.
    for (const a of want) ownByInFlight.add(a);
    void (async () => {
      try {
        const owned = await get().repo.fetchCrewOwnedBy(want);
        set((s) => {
          // SEEDED FIRST, THEN MERGED — see the field's doc-comment. Every appid
          // we asked about gets an entry even when nobody owns it, because
          // "asked, nobody owns it" is the answer that hides a row and silence
          // is the one that must not.
          const next: Record<number, string[]> = { ...s.ownBy };
          for (const a of want) next[a] = owned[a] ?? [];
          return { ownBy: next };
        });
      } catch (e) {
        // NOTHING CACHED, deliberately — the opposite of `ensureCrewOwnership`
        // next door. That one caches its empty answer because a render path
        // re-asks it per appid and would otherwise hammer the RPC; this is
        // called from a panel open and a click. Caching `[]` here would mean a
        // single failed read renders as "nobody owns anything" and empties
        // every board for the rest of the session, which is a fabrication, not
        // a degraded state. Left unasked, it simply retries on the next toggle.
        //
        // BUT NOT SILENTLY, and that half was learned the hard way. Not caching
        // is the right call and it is also what makes a failure here look like
        // nothing at all: `passFlt` reads an un-answered appid as
        // ownership-UNKNOWN and never excludes on it, so a broken read presents
        // as chips that light up over a board and a count that do not move.
        // That is precisely how the unbound-`rpc` defect
        // (`SupabaseRepository.binding.test.ts`) survived a deploy. A degraded
        // filter has to be diagnosable from the console at least.
        console.error('[qd] crew ownership read failed — OWNED BY will not narrow', e);
      } finally {
        for (const a of want) ownByInFlight.delete(a);
      }
    })();
  },

  ensureMyLibrary: (appids) => {
    const have = get().myLib;
    const want = [
      ...new Set(
        appids.filter(
          (a) => Number.isSafeInteger(a) && a > 0 && !(a in have) && !myLibInFlight.has(a),
        ),
      ),
    ];
    if (want.length === 0) return;
    // In flight BEFORE the await, as next door — and it matters more here,
    // because both BACKLOG screens call this from a RENDER rather than from a
    // panel open. Without it, every re-render while the read is in the air
    // would launch another copy of it.
    for (const a of want) myLibInFlight.add(a);
    void (async () => {
      try {
        const owned = await get().repo.fetchMyLibrary(want);
        set((s) => {
          // SEEDED FIRST, THEN MERGED, exactly as `ensureOwnedBy` does and for
          // the same reason the field's doc-comment gives: everything asked
          // about gets an answer, so `null` can mean "asked, you do not own it"
          // and absence can keep meaning "not asked". An owners-only merge would
          // leave the unowned games indistinguishable from the unread ones, and
          // UNOWNED is precisely the tab that has to tell them apart.
          const next: Record<number, number | null> = { ...s.myLib };
          for (const a of want) next[a] = owned[a] ?? null;
          return { myLib: next };
        });
      } catch (e) {
        // NOTHING CACHED — the same call `ensureOwnedBy` makes, and here the
        // fabrication it avoids is louder: a cached failure would read as "you
        // own none of these", filing the member's entire shelf under UNOWNED
        // and showing PLAYED and OWNED as empty, all of it looking like a real
        // answer. Left unasked, the tabs stay honestly pending and the next
        // visit re-asks.
        //
        // AND NOT SILENTLY, for the reason recorded next door: a degraded tab
        // is indistinguishable from an empty one on screen, so the console is
        // the only place this is diagnosable.
        console.error('[qd] steam_owned_apps read failed — the BACKLOG tabs stay pending', e);
      } finally {
        for (const a of want) myLibInFlight.delete(a);
      }
    })();
  },

  ensureCrewLinks: () => {
    if (get().crewLinks) return;
    void (async () => {
      try {
        const rows = await get().repo.fetchCrewSteamLinks();
        const map: Record<string, boolean> = {};
        for (const r of rows) map[r.userKey] = r.linked;
        set({ crewLinks: map });
      } catch {
        // Left `null` ON PURPOSE, which is the opposite of what the two
        // neighbours above do. They cache their empty answer because a render
        // path re-asks; this is reached from a nav, so leaving it unanswered
        // costs one retry per visit to Friends and buys the guarantee that a
        // failed read can never paint as `STEAM NOT LINKED`.
      }
    })();
  },

  ensurePlayerOverride: (appid) => {
    if (!Number.isSafeInteger(appid) || appid <= 0) return;
    // PRESENCE, not truthiness — `null` is the "asked, no override" answer and
    // a truthy guard would re-ask forever. See the field's doc-comment.
    if (appid in get().ovr) return;
    void (async () => {
      try {
        const o = await get().repo.fetchPlayerOverride(appid);
        set((s) => ({ ovr: { ...s.ovr, [appid]: o } }));
      } catch {
        // A failed read is recorded as "no override" rather than left absent:
        // retrying on every render is worse than a missing attribution line,
        // and the count itself is unaffected either way.
        set((s) => ({ ovr: { ...s.ovr, [appid]: null } }));
      }
    })();
  },

  setPlayerOverrideCache: (appid, o) => set((s) => ({ ovr: { ...s.ovr, [appid]: o } })),

  ensureCcuSeries: (appid) => {
    if (!Number.isSafeInteger(appid) || appid <= 0) return;
    if (get().ccuSeries[appid]) return;
    void (async () => {
      try {
        const res = await get().repo.fetchCcuSeries(appid);
        set((s) => ({ ccuSeries: { ...s.ccuSeries, [appid]: res } }));
      } catch {
        // A missing chart is a missing chart. Cache the empty answer so a
        // detail screen that re-renders does not re-ask on every paint; the
        // next session tries again.
        set((s) => ({ ccuSeries: { ...s.ccuSeries, [appid]: { points: [], sample: false } } }));
      }
    })();
  },

  ensurePriceSeries: (appid) => {
    if (!Number.isSafeInteger(appid) || appid <= 0) return;
    if (get().priceSeries[appid]) return;
    void (async () => {
      try {
        const res = await get().repo.fetchPriceSeries(appid);
        set((s) => ({ priceSeries: { ...s.priceSeries, [appid]: res } }));
      } catch {
        // Same rule as the chart above, and the same reason: cache the empty
        // answer so a re-render does not re-ask, and let the next session retry.
        set((s) => ({ priceSeries: { ...s.priceSeries, [appid]: { points: [], sample: false } } }));
      }
    })();
  },

  ensureDetailFacts: (appid) => {
    if (!Number.isSafeInteger(appid) || appid <= 0) return;
    // PRESENCE, not truthiness — `null` is the "asked, no row" answer and a
    // truthy guard would re-ask forever. Same rule as `ovr`.
    if (appid in get().detailFacts) return;
    void (async () => {
      try {
        const f = await get().repo.fetchDetailFacts(appid);
        set((s) => ({ detailFacts: { ...s.detailFacts, [appid]: f } }));
      } catch {
        // Cached as "no row", which renders as the pending state — the honest
        // reading of a failed lookup, and it stops a re-ask on every paint.
        set((s) => ({ detailFacts: { ...s.detailFacts, [appid]: null } }));
      }
    })();
  },

  /**
   * THE §5.3/§5.4 ENTRY POINT: refresh what is on screen, on a time floor.
   *
   * WHY THIS EXISTS AT ALL — the fault it replaces is the reason the app's own
   * revalidation machinery had been dead on its main screen since Discover
   * became a server-paged feed.
   *
   * `queueAssets` used to dedupe on `s.assets`: an appid whose art record was
   * already in the map never joined the batch. That looks like an obvious
   * saving, and it is the whole bug, because both Discover paths PRE-FILL
   * `s.assets` from the art that arrives WITH the rows (`slices/feed.ts` and
   * `slices/browse.ts` both merge `page.assets` into the map). Every tile on the
   * default screen was therefore deduped out before it could reach
   * `/api/steam/items` — the endpoint whose ribbons, price and review percent
   * exist to refresh exactly those tiles was never called for one of them. The
   * old comment here claimed the §5.3 ladder covered it via `revalidateVisible`;
   * that was the second half of the same fault, since `revalidateVisible`
   * revalidated the crew list plus `catalog.slice(0, 12)` and the catalog is not
   * what Discover renders. Two plausible-looking guards, and between them the
   * SWR ladder ran on nothing a user was looking at.
   *
   * The lesson is the dedupe key. ART PRESENCE SAYS NOTHING ABOUT STALENESS:
   * a capsule URL is immutable for years, while price, discount, EARLY ACCESS
   * and the review percentage are exactly the fields that move — and they now
   * ride on the same record. Conflating "I have a picture" with "I know the
   * price" is what silently turned a refresh into a no-op. The only honest key
   * is when we last asked (`SURFACE_MIN_MS`), which is also the only one that
   * stays correct when the payload gains another volatile field.
   *
   * Three things happen for the appids that are actually due:
   *   items  the first `GI_MAX_BATCH` go out NOW (a focus pass that resolved
   *          only after a coalesce timer could not report its own outcome to the
   *          freshness chip), the tail through the existing coalescing drain.
   *   ccu    the head gets the live-count overlay — display only, see `runCcu`.
   *   stamp  every appid asked about is stamped, failure included: the retry
   *          ladder in `revalidate` owns retries, and re-asking on the next
   *          render instead would be the hammering this floor exists to stop.
   */
  revalidateSurface: async (appids) => {
    const now = Date.now();
    const due: number[] = [];
    for (const a of appids) {
      if (!Number.isSafeInteger(a) || a <= 0) continue;
      // Stamping inside the loop also dedupes WITHIN one call: the second copy
      // of a repeated appid is now `now - now = 0` old.
      if (now - (revalAt.get(a) ?? 0) < SURFACE_MIN_MS) continue;
      revalAt.set(a, now);
      due.push(a);
    }
    if (!due.length) return;

    const tail = due.slice(GI_MAX_BATCH);
    if (tail.length) {
      for (const a of tail) if (!giQ.includes(a)) giQ.push(a);
      if (giT) clearTimeout(giT);
      giT = setTimeout(giDrain, GI_COALESCE_MS);
    }
    // The overlay runs beside the items batch, not after it: it answers a
    // different question, and it must not be able to delay the ribbons.
    void runCcu(due.slice(0, CCU_SURFACE_MAX));
    await get().revalidate(due.slice(0, GI_MAX_BATCH));
  },

  /**
   * The name the tile surfaces call (Discover's screenful effect, GOTY, the
   * auto-fill's new potentials). It IS `revalidateSurface` — art and the
   * volatile set come back in one record from one endpoint, so there is nothing
   * left for a second, art-only path to mean.
   */
  queueAssets: (appids) => {
    void get().revalidateSurface(appids);
  },

  revalidate: async (appids) => {
    // This batch's own pending retry, and only this batch's — see `retryT`.
    // Clearing it here is what keeps the ladder from doubling when a retry and a
    // fresh pass for the same appids overlap.
    const rKey = appids.join(',');
    const pending = retryT.get(rKey);
    if (pending !== undefined) {
      clearTimeout(pending);
      retryT.delete(rKey);
    }
    if (!appids.length) return;
    const s = get();
    s.freshBusy();
    let ok = false;
    let assets: AssetMap = {};
    try {
      const res = await s.repo.giFlush(appids);
      assets = res.assets;
      ok = res.fromNetwork;
    } catch {
      ok = false;
    }
    if (Object.keys(assets).length) {
      // The same batched call that fetches a screenful of art also reports
      // UPCOMING / EARLY ACCESS / DEMO, the price and the discount, and the
      // review percentage — the only way a tile nobody has opened gets any of
      // them, and the only refresh a tile on Discover ever gets.
      const patches = itemPatches(assets);
      const keys = Object.keys(patches);
      /**
       * Gap-fills for `upsert_steam_facts`, decided against the PRE-PATCH rows.
       *
       * Read here rather than inside the `set` updater below, and the ordering
       * is the whole reason: `writebackGaps` asks "did this row have a price
       * *before* this response arrived", and one line into that updater the
       * answer is yes for every row in the batch. Computing it out here keeps
       * the comparison honest AND keeps the updater a pure function of state,
       * which is the property that makes it safe to reason about at all.
       */
      const heal: SteamFactPatch[] = [];
      if (keys.length) {
        const pre = get();
        for (const k of keys) {
          const p = patches[k];
          if (!p) continue;
          const appid = Number(k);
          const row =
            pre.catalog.find((g) => g.appid === appid) ??
            pre.feedRows.find((g) => g.appid === appid) ??
            pre.live.find((g) => g.appid === appid);
          const gap = writebackGaps(row, p);
          if (gap) heal.push(gap);
        }
      }
      set((st) => {
        const next = { assets: { ...st.assets, ...assets } };
        if (!keys.length) return next;
        const facts = { ...st.facts };
        for (const k of keys) {
          // Field-level merge: an items response that says nothing about demos
          // must not undo a `demo: true` an appdetails enrich already stored.
          facts[k] = { ...st.facts[k], ...patches[k], appid: Number(k) };
        }
        const patch = (g: Game): Game => {
          const key = String(g.appid);
          const f = patches[key] ? facts[key] : undefined;
          return f ? applyFact(g, f) : g;
        };
        // Feed tiles queue their own appids through `queueAssets`, so the
        // ribbon half of this batch describes them as much as it does the
        // catalog and live rows — same reasoning as `applyEnrich` above.
        return {
          ...next,
          facts,
          catalog: st.catalog.map(patch),
          live: st.live.map(patch),
          feedRows: st.feedRows.map(patch),
        };
      });
      // One debounced batch for the whole screenful, sharing `applyEnrich`'s
      // queue and timer so a Detail open and a surface pass cannot race each
      // other into two RPCs. Empty on a screen whose rows the pool has already
      // filled, which is the common case and the intent — see `writebackGaps`.
      if (heal.length) {
        fixQ.push(...heal);
        if (fixT) clearTimeout(fixT);
        fixT = setTimeout(() => {
          const batch = fixQ;
          fixQ = [];
          fixT = null;
          void get().repo.saveFix(batch);
        }, SAVE_FIX_DEBOUNCE_MS);
      }
    }
    if (ok) {
      get().freshOk();
      return;
    }
    get().freshFail('UPSTREAM_STATUS');
    const failures = get().freshness.failures;
    // BACKOFF_CAP capped the DELAY; nothing capped the COUNT, so a backgrounded
    // offline tab re-tried forever. Past OFFLINE_AFTER the ladder is only noise:
    // the chip already reads OFFLINE, and the next focus / queueAssets / boot
    // pass restarts it for free.
    if (failures >= OFFLINE_AFTER) return;
    retryT.set(
      rKey,
      setTimeout(() => {
        retryT.delete(rKey);
        void get().revalidate(appids);
      }, backoffMs(failures)),
    );
  },

  cancelRevalidate: () => {
    for (const t of retryT.values()) clearTimeout(t);
    retryT.clear();
    if (giT) clearTimeout(giT);
    if (fixT) clearTimeout(fixT);
    giT = null;
    fixT = null;
    giQ = [];
    fixQ = [];
    // The teardown path: a new session must re-enrich, must re-revalidate every
    // surface it paints, and must be free to re-ask even if a prior request
    // never settled.
    enriched.clear();
    inFlight.clear();
    revalAt.clear();
    ownByInFlight.clear();
    myLibInFlight.clear();
  },
  };
};
